import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

// Hero background
import heroBg from "../../../images/bg/asic-soc-hero-bg.jpg";

import "./ASICSoCDesign.css";
import "../CoreServicesTheme.css";
const ASICDesignHeroSection: React.FC = () => (
  <section
    className="asx-hero"
    style={{ backgroundImage: `url(${heroBg})` }}
  >
    <div className="asx-hero__overlay" aria-hidden="true" />
    <div className="asx-hero__glow" aria-hidden="true" />
    <div className="asx-hero__grid" aria-hidden="true" />

    <div className="container mxw-1650">
      <div className="asx-hero__layout">
        <div className="asx-hero__content">
          <h1>Engineering Robust ASIC &amp; SoC Designs</h1>

          <p>
            ASIC &amp; SoC design forms the foundation of every successful
            semiconductor product. Digicomm Semiconductor delivers specialized
            ASIC &amp; SoC design expertise that transforms architectural intent
            into reliable, scalable, and implementation-ready digital solutions.
            Through disciplined execution, technical excellence, and collaborative
            engineering, we help semiconductor organizations develop
            high-performance products that meet evolving functionality, quality,
            and market requirements.
          </p>

          <Link to="/contact" className="asx-hero__button">
            <span>Discuss Your ASIC &amp; SoC Design Requirements</span>
            <i aria-hidden="true">↗</i>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const ServiceOverviewSection: React.FC = () => {
  const journeyStages = [
    { number: "01", title: "Architecture", icon: "architecture" },
    {
      number: "02",
      title: "ASIC & SoC Design",
      icon: "chip",
      active: true,
    },
    { number: "03", title: "Verification", icon: "verification" },
    { number: "04", title: "Physical Design", icon: "physical" },
    { number: "05", title: "Silicon Realization", icon: "silicon" },
  ];

  return (
    <section className="asx-overview-v3">
      {/* Decorative right-side semiconductor visual */}
      <div className="asx-overview-v3__visual" aria-hidden="true">
        <div className="asx-overview-v3__visual-glow" />

        <div className="asx-overview-v3__layer asx-overview-v3__layer--1">
          <span>ASIC | SOC</span>
        </div>

        <div className="asx-overview-v3__layer asx-overview-v3__layer--2" />

        <div className="asx-overview-v3__layer asx-overview-v3__layer--3" />

        <div className="asx-overview-v3__layer asx-overview-v3__layer--4" />
      </div>

      <div className="container mxw-1650">
        {/* ================= TOP ================= */}
        <div className="asx-overview-v3__top">
          {/* LEFT */}
          <div className="asx-overview-v3__heading">
            <div className="asx-overview-v3__eyebrow">
              <span>Service Overview</span>
              <i />
              <b />
            </div>

            <h2>
              ASIC &amp; SoC Design
              <br />
              Service Overview
            </h2>

            <div className="asx-overview-v3__accent">
              <span />
              <span />
              <span />
            </div>
          </div>

          {/* RIGHT */}
          <div className="asx-overview-v3__content">
            <span className="asx-overview-v3__dot" />

            <p>
              ASIC &amp; SoC design is the foundation of modern semiconductor
              product development. It establishes the architecture,
              functionality, and digital framework that determine how a
              semiconductor device performs throughout its lifecycle. As design
              complexity continues to increase, successful ASIC &amp; SoC
              development demands architectural insight, engineering precision,
              disciplined execution, and close collaboration across multiple
              engineering disciplines.
            </p>

            <p>
              Digicomm Semiconductor delivers specialized ASIC &amp; SoC Design
              Services that form the starting point of its Core Engineering
              Services portfolio. From architecture support, RTL implementation,
              and microarchitecture development to IP integration and design
              optimization, our engineering teams work as a seamless extension
              of customer organizations to develop reliable, scalable, and
              implementation-ready designs that enable successful silicon
              realization.
            </p>
          </div>
        </div>

        {/* ================= JOURNEY ================= */}
        <div className="asx-overview-v3__journey">
          <div
            className="asx-overview-v3__journey-line"
            aria-hidden="true"
          />

          {journeyStages.map((stage) => (
            <div
              key={stage.number}
              className={`asx-overview-v3__stage ${
                stage.active ? "asx-overview-v3__stage--active" : ""
              }`}
            >
              <div className="asx-overview-v3__number">
                {stage.number}
              </div>

              <h3>{stage.title}</h3>

              {/* ARCHITECTURE */}
              {stage.icon === "architecture" && (
                <div
                  className="asx-overview-v3__architecture"
                  aria-hidden="true"
                >
                  <span />
                  <span />
                  <span />
                  <span />

                  <i className="line-h" />
                  <i className="line-v" />
                </div>
              )}

              {/* ASIC CHIP */}
              {stage.icon === "chip" && (
                <div
                  className="asx-overview-v3__chip"
                  aria-hidden="true"
                >
                  <div />
                </div>
              )}

              {/* VERIFICATION */}
              {stage.icon === "verification" && (
                <div
                  className="asx-overview-v3__verification"
                  aria-hidden="true"
                >
                  <div className="asx-overview-v3__clipboard">
                    <span>✓</span>
                    <span>✓</span>
                  </div>

                  <div className="asx-overview-v3__check-circle">
                    ✓
                  </div>
                </div>
              )}

              {/* PHYSICAL DESIGN */}
              {stage.icon === "physical" && (
                <div
                  className="asx-overview-v3__physical"
                  aria-hidden="true"
                >
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              )}

              {/* SILICON */}
              {stage.icon === "silicon" && (
                <div
                  className="asx-overview-v3__silicon"
                  aria-hidden="true"
                >
                  {Array.from({ length: 16 }).map((_, index) => (
                    <span key={index} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyItMattersSection: React.FC = () => {
  const outcomes = [
    {
      title: "Establishes a robust and scalable design foundation",
      icon: "layers",
    },
    {
      title: "Improves design quality and engineering consistency",
      icon: "shield",
    },
    {
      title: "Reduces downstream implementation and verification risks",
      icon: "warning",
    },
    {
      title: "Supports predictable development timelines",
      icon: "clock",
    },
    {
      title: "Contributes to reliable silicon realization",
      icon: "chip",
    },
  ];

  const OutcomeIcon = ({ type }: { type: string }) => {
    if (type === "layers") {
      return (
        <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
          <path d="M32 13 51 23 32 33 13 23 32 13Z" />
          <path d="M15 31 32 40 49 31" />
          <path d="M15 40 32 49 49 40" />
        </svg>
      );
    }

    if (type === "shield") {
      return (
        <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
          <path d="M32 10 49 16V29C49 41 42 49 32 54 22 49 15 41 15 29V16L32 10Z" />
          <path d="m23 31 6 6 13-14" />
        </svg>
      );
    }

    if (type === "warning") {
      return (
        <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
          <path d="M32 11 54 51H10L32 11Z" />
          <path d="M32 24V37" />
          <circle cx="32" cy="44.5" r="1.7" className="fill-dot" />
        </svg>
      );
    }

    if (type === "clock") {
      return (
        <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
          <circle cx="32" cy="32" r="21" />
          <path d="M32 19V33L41 39" />
        </svg>
      );
    }

    return (
      <svg viewBox="0 0 64 64" role="img" aria-hidden="true">
        <rect x="17" y="17" width="30" height="30" rx="3" />
        <rect x="23" y="23" width="18" height="18" rx="1.5" />
        <path d="M23 10V17M32 10V17M41 10V17M23 47V54M32 47V54M41 47V54M10 23H17M10 32H17M10 41H17M47 23H54M47 32H54M47 41H54" />
      </svg>
    );
  };

  return (
    <section className="asx-matters-v2">
      <div className="asx-matters-v2__circuit" aria-hidden="true">
        <span className="asx-matters-v2__circuit-line asx-matters-v2__circuit-line--1" />
        <span className="asx-matters-v2__circuit-line asx-matters-v2__circuit-line--2" />
        <span className="asx-matters-v2__circuit-line asx-matters-v2__circuit-line--3" />
        <span className="asx-matters-v2__circuit-node asx-matters-v2__circuit-node--1" />
        <span className="asx-matters-v2__circuit-node asx-matters-v2__circuit-node--2" />
      </div>

      <div className="asx-matters-v2__chip-art" aria-hidden="true">
        <div className="asx-matters-v2__chip-art-glow" />
        <div className="asx-matters-v2__chip-art-layer asx-matters-v2__chip-art-layer--1" />
        <div className="asx-matters-v2__chip-art-layer asx-matters-v2__chip-art-layer--2" />
        <div className="asx-matters-v2__chip-art-core" />
      </div>

      <div className="container mxw-1650">
        <div className="asx-matters-v2__layout">
          <div className="asx-matters-v2__content">
            <div className="asx-matters-v2__eyebrow">
              <span>Why It Matters</span>
              <i />
              <b />
            </div>

            <h2>Why It Matters</h2>

            <div className="asx-matters-v2__accent" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>

            <p>
              ASIC &amp; SoC design establishes the engineering foundation of every semiconductor
              product. Decisions made during this stage influence system performance, power
              efficiency, scalability, verification complexity, physical implementation,
              manufacturability, and ultimately the success of silicon realization. A well-engineered
              ASIC &amp; SoC design not only reduces downstream engineering challenges but also
              improves design quality, strengthens engineering predictability, and builds greater
              confidence throughout the semiconductor development lifecycle.
            </p>
          </div>

          <div className="asx-matters-v2__right">
            <div className="asx-matters-v2__right-heading">
              <div className="asx-matters-v2__target-icon" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>

              <h3>Key Outcomes</h3>
              <div className="asx-matters-v2__right-line" aria-hidden="true" />
            </div>

            <div className="asx-matters-v2__timeline">
              <div className="asx-matters-v2__timeline-rail" aria-hidden="true" />

              {outcomes.map((outcome) => (
                <article className="asx-matters-v2__outcome" key={outcome.title}>
                  <div className="asx-matters-v2__timeline-dot" aria-hidden="true" />

                  <div className="asx-matters-v2__outcome-icon" aria-hidden="true">
                    <OutcomeIcon type={outcome.icon} />
                  </div>

                  <p>{outcome.title}</p>

                  <span className="asx-matters-v2__arrow" aria-hidden="true">
                    ↗
                  </span>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const CapabilitiesSection: React.FC = () => {
  const capabilities = [
    {
      title: "RTL Design",
      description:
        "Developing robust, synthesizable Register Transfer Level (RTL) designs that establish the functional foundation for reliable and scalable semiconductor products.",
      icon: "rtl",
    },
    {
      title: "Digital Design",
      description:
        "Supporting the development of digital logic and functional architectures that meet performance, power, and reliability objectives across diverse semiconductor applications.",
      icon: "digital",
    },
    {
      title: "SoC Design Support",
      description:
        "Supporting complex System-on-Chip (SoC) development through subsystem integration, design implementation, and collaborative engineering across multidisciplinary development teams.",
      icon: "soc",
    },
    {
      title: "IP Integration",
      description:
        "Integrating third-party and custom IP blocks into SoC architectures while ensuring functional compatibility, design integrity, and implementation readiness.",
      icon: "ip",
    },
    {
      title: "Microarchitecture Development",
      description:
        "Translating architectural specifications into efficient microarchitectures that balance functionality, performance, power, and implementation feasibility.",
      icon: "micro",
    },
    {
      title: "Design Optimization",
      description:
        "Optimizing digital designs for performance, power, area, timing, and implementation quality to support reliable and efficient silicon realization.",
      icon: "optimization",
    },
  ];

  const CapabilityIcon = ({ type }: { type: string }) => {
    if (type === "rtl") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <rect x="10" y="39" width="11" height="11" rx="1" />
          <rect x="43" y="10" width="11" height="11" rx="1" />
          <path d="M21 44H31V16H43" />
          <path d="M31 29H23" />
          <circle cx="21" cy="29" r="2" className="cap-fill" />
        </svg>
      );
    }

    if (type === "digital") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <path d="M10 20H22C32 20 42 25 48 32C42 39 32 44 22 44H10" />
          <path d="M22 20C28 25 31 29 31 32C31 35 28 39 22 44" />
          <path d="M48 32H55" />
        </svg>
      );
    }

    if (type === "soc") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <rect x="16" y="16" width="32" height="32" rx="3" />
          <path d="M22 9V16M32 9V16M42 9V16M22 48V55M32 48V55M42 48V55M9 22H16M9 32H16M9 42H16M48 22H55M48 32H55M48 42H55" />
          <text x="32" y="36" textAnchor="middle" className="soc-label">SoC</text>
        </svg>
      );
    }

    if (type === "ip") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <path d="M12 14H27V22C27 26 31 26 31 22V14H48V29H40C36 29 36 33 40 33H48V50H33V42C33 38 29 38 29 42V50H12V35H20C24 35 24 31 20 31H12V14Z" />
        </svg>
      );
    }

    if (type === "micro") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <rect x="27" y="9" width="10" height="10" />
          <rect x="9" y="43" width="10" height="10" />
          <rect x="27" y="43" width="10" height="10" />
          <rect x="45" y="43" width="10" height="10" />
          <path d="M32 19V31M14 31H50M14 31V43M32 31V43M50 31V43" />
        </svg>
      );
    }

    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="10" y="38" width="8" height="16" />
        <rect x="22" y="31" width="8" height="23" />
        <rect x="34" y="23" width="8" height="31" />
        <rect x="46" y="14" width="8" height="40" />
        <path d="M10 54H55" />
      </svg>
    );
  };

  return (
    <section className="asx-capabilities-v2">
      <div className="asx-capabilities-v2__grid-bg" aria-hidden="true" />

      <div className="container mxw-1650">
        <div className="asx-capabilities-v2__header">
          <div className="asx-capabilities-v2__eyebrow">
            <span>Our Capabilities</span>
            <i />
            <b />
          </div>

          <h2>
            ASIC &amp; SoC Design
            <br />
            Capabilities
          </h2>

          <div className="asx-capabilities-v2__accent" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>

          <p>
            Digicomm&apos;s core ASIC &amp; SoC Design capabilities span the entire digital design
            spectrum—delivered with engineering excellence, disciplined execution, and a focus on
            quality, performance, and scalability. We partner with semiconductor companies to build
            reliable, efficient, and future-ready silicon solutions.
          </p>
        </div>

        <div className="asx-capabilities-v2__grid">
          {capabilities.map((capability) => (
            <article className="asx-capabilities-v2__card" key={capability.title}>
              <div className="asx-capabilities-v2__icon" aria-hidden="true">
                <span className="asx-capabilities-v2__icon-ring" />
                <CapabilityIcon type={capability.icon} />
              </div>

              <div className="asx-capabilities-v2__card-content">
                <h3>{capability.title}</h3>

                <div className="asx-capabilities-v2__mini-line" aria-hidden="true">
                  <span />
                </div>

                <p>{capability.description}</p>
              </div>

              <span className="asx-capabilities-v2__arrow" aria-hidden="true">
                ↗
              </span>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const KeyBenefitsSection: React.FC = () => {
  const benefits = [
    {
      title: "Engineering Confidence",
      description:
        "Structured engineering practices that improve design quality and reduce development uncertainty.",
      icon: "confidence",
      className: "",
    },
    {
      title: "Accelerated Development",
      description:
        "Optimized design methodologies that support efficient engineering execution and predictable project timelines.",
      icon: "speed",
      className: "",
    },
    {
      title: "Scalable Digital Architectures",
      description:
        "Design solutions developed to accommodate increasing product complexity, evolving technologies, and future scalability.",
      icon: "layers",
      className: "",
    },
    {
      title: "Improved Design Quality",
      description:
        "Disciplined engineering reviews and design practices that contribute to functional correctness and implementation readiness.",
      icon: "quality",
      className: "",
    },
    {
      title: "Collaborative Engineering",
      description:
        "Seamless integration with customer engineering teams through transparent communication and shared execution ownership.",
      icon: "team",
      className: "",
    },
    {
      title: "Reliable Project Outcomes",
      description:
        "A consistent focus on disciplined execution, technical excellence, and dependable engineering delivery throughout the design lifecycle.",
      icon: "target",
      className: "",
    },
  ];

  const BenefitIcon = ({ type }: { type: string }) => {
    if (type === "confidence") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <path d="M32 8 49 15V29c0 11-7 20-17 26-10-6-17-15-17-26V15L32 8Z" />
          <path d="m23 31 6 6 13-14" />
        </svg>
      );
    }

    if (type === "speed") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <path d="M14 43a21 21 0 1 1 36 0" />
          <path d="M32 32 44 22" />
          <path d="M17 27h5M42 16l-3 5M32 11v6M47 30h5" />
          <circle cx="32" cy="32" r="3" className="benefit-fill" />
        </svg>
      );
    }

    if (type === "layers") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <path d="M32 12 51 22 32 32 13 22 32 12Z" />
          <path d="M15 31 32 40 49 31" />
          <path d="M15 40 32 49 49 40" />
        </svg>
      );
    }

    if (type === "quality") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="25" r="13" />
          <path d="m32 16 3 6 7 .9-5 4.8 1.4 6.8-6.4-3.3-6.4 3.3 1.4-6.8-5-4.8 7-.9 3-6Z" />
          <path d="M24 36 19 52l8-5 5 7 3-15M40 36l5 16-8-5-5 7" />
        </svg>
      );
    }

    if (type === "team") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="20" r="7" />
          <circle cx="17" cy="24" r="5" />
          <circle cx="47" cy="24" r="5" />
          <path d="M21 47v-4c0-8 5-13 11-13s11 5 11 13v4" />
          <path d="M7 46v-3c0-7 4-11 10-11 3 0 5 1 7 3M57 46v-3c0-7-4-11-10-11-3 0-5 1-7 3" />
        </svg>
      );
    }

    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="27" cy="35" r="18" />
        <circle cx="27" cy="35" r="11" />
        <circle cx="27" cy="35" r="4" />
        <path d="M31 31 50 12M42 13h9v9M49 13l-8 8" />
      </svg>
    );
  };

  return (
    <section className="asx-benefits-v2">
      <div className="container mxw-1650">
        <div className="asx-benefits-v2__header">
          <div className="asx-benefits-v2__heading">
            <div className="asx-benefits-v2__eyebrow">
              <span>Key Benefits</span>
              <i />
              <b />
            </div>

            <h2>Key Benefits</h2>

            <div className="asx-benefits-v2__accent" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          </div>

          <p>
            Robust digital design creates the foundation for successful semiconductor products.
            Digicomm&apos;s Design Services help organizations strengthen engineering execution,
            improve design quality, and build reliable digital architectures that support long-term
            product success.
          </p>
        </div>

        <div className="asx-benefits-v2__grid">
          {benefits.map((benefit) => (
            <article
              key={benefit.title}
              className={`asx-benefit-v2 ${benefit.className}`}
            >
              <span className="asx-benefit-v2__corner" aria-hidden="true">
                ↗
              </span>

              <div className="asx-benefit-v2__icon" aria-hidden="true">
                <span className="asx-benefit-v2__icon-ring" />
                <BenefitIcon type={benefit.icon} />
              </div>

              <div className="asx-benefit-v2__divider" aria-hidden="true" />

              <div className="asx-benefit-v2__content">
                <h3>{benefit.title}</h3>

                <div className="asx-benefit-v2__mini-line" aria-hidden="true">
                  <span />
                  <span />
                </div>

                <p>{benefit.description}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const EngineeringApproachSection: React.FC = () => {
  const workflow = [
    { number: "01", title: "Understand", icon: "understand", description: "Develop a clear understanding of product objectives, design requirements, technical specifications, and project expectations." },
    { number: "02", title: "Plan", icon: "plan", description: "Define the engineering scope, execution strategy, milestones, resource planning, and delivery framework." },
    { number: "03", title: "Engineer", icon: "engineer", description: "Execute design activities through structured engineering practices, technical reviews, and collaborative development." },
    { number: "04", title: "Validate", icon: "validate", description: "Perform continuous design reviews, quality checks, and engineering validations to ensure implementation readiness." },
    { number: "05", title: "Deliver", icon: "deliver", description: "Provide implementation-ready design deliverables with a focus on quality, reliability, and predictable engineering outcomes." },
  ];

  const WorkflowIcon = ({ type }: { type: string }) => {
    if (type === "understand") return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="28" cy="28" r="15" /><path d="m39 39 12 12" /><circle cx="28" cy="28" r="10" />
      </svg>
    );
    if (type === "plan") return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="16" y="14" width="32" height="38" rx="3" />
        <path d="M24 14v-4h16v4M22 25l3 3 5-6M22 35l3 3 5-6M34 25h8M34 35h8M22 45h20" />
      </svg>
    );
    if (type === "engineer") return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="10" />
        <path d="M32 10v7M32 47v7M10 32h7M47 32h7M16.5 16.5l5 5M42.5 42.5l5 5M47.5 16.5l-5 5M21.5 42.5l-5 5" />
        <path d="M27 12h10l2 6 6 3 6-2 5 8-5 4v7l5 4-5 8-6-2-6 3-2 6H27l-2-6-6-3-6 2-5-8 5-4v-7l-5-4 5-8 6 2 6-3 2-6Z" />
      </svg>
    );
    if (type === "validate") return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <path d="M32 10 49 16v14c0 11-7 19-17 24-10-5-17-13-17-24V16l17-6Z" /><path d="m23 31 6 6 13-14" />
      </svg>
    );
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <path d="M20 39 12 47l8 1 1 8 8-8" />
        <path d="M38 26c-5-5-10-7-15-6 1 5 3 10 8 15s10 7 15 6c1-5-1-10-8-15Z" />
        <path d="M42 22 52 12M41 15l11-3-3 11M30 36l-7 7" /><circle cx="37" cy="27" r="3" />
      </svg>
    );
  };

  return (
    <section className="asx-approach-v2">
      <div className="container mxw-1650">
        <div className="asx-approach-v2__header">
          <div className="asx-approach-v2__accent" aria-hidden="true"><span /><span /><span /></div>
          <h2>Engineering Approach</h2>
          <p>
            Successful ASIC &amp; SoC design requires more than technical expertise—it demands a structured engineering methodology that transforms architectural intent into implementation-ready designs. Digicomm&apos;s engineering approach combines disciplined planning, collaborative development, continuous design reviews, and quality-driven execution to deliver robust, scalable, and reliable digital designs that support successful downstream engineering activities.
          </p>
        </div>

        <div className="asx-approach-v2__panel">
          <h3>ASIC &amp; SoC Design Engineering Workflow</h3>
          <div className="asx-approach-v2__flow">
            {workflow.map((step, index) => (
              <React.Fragment key={step.title}>
                <article className="asx-approach-v2__step">
                  <div className="asx-approach-v2__circle">
                    <span className="asx-approach-v2__number">{step.number}</span>
                    <div className="asx-approach-v2__icon" aria-hidden="true"><WorkflowIcon type={step.icon} /></div>
                  </div>
                  <div className="asx-approach-v2__micro-accent" aria-hidden="true"><span /><span /></div>
                  <h4>{step.title}</h4>
                  <div className="asx-approach-v2__title-line" aria-hidden="true" />
                  <p>{step.description}</p>
                </article>
                {index < workflow.length - 1 && (
                  <div className="asx-approach-v2__connector" aria-hidden="true">
                    <span className="asx-approach-v2__connector-dot" />
                    <span className="asx-approach-v2__connector-line" />
                    <span className="asx-approach-v2__connector-arrow">»</span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TechnologyExpertiseSection: React.FC = () => {
  const technologyGroups = [
    {
      title: "Engineering Domains",
      icon: "chip",
      items: [
        "Digital IC Design",
        "SoC Design",
        "ASIC Design",
        "FPGA Design Support",
        "IP & Subsystem Integration",
      ],
    },
    {
      title: "Design Methodologies",
      icon: "gear",
      items: [
        "RTL Design",
        "Microarchitecture Development",
        "Low-Power Design Techniques",
        "Design Optimization",
        "Design Reviews & Quality Practices",
      ],
    },
    {
      title: "Industry Standards & Protocols",
      icon: "shield",
      items: [
        "AMBA / AXI",
        "PCIe",
        "USB",
        "Ethernet",
        "DDR Memory Interfaces",
        "MIPI",
      ],
    },
    {
      title: "EDA Environment",
      icon: "monitor",
      items: ["Cadence", "Synopsys", "Siemens EDA"],
    },
  ];

  const TechnologyIcon = ({ type }: { type: string }) => {
    if (type === "chip") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <rect x="18" y="18" width="28" height="28" rx="3" />
          <rect x="24" y="24" width="16" height="16" rx="2" />
          <path d="M24 10v8M32 10v8M40 10v8M24 46v8M32 46v8M40 46v8M10 24h8M10 32h8M10 40h8M46 24h8M46 32h8M46 40h8" />
        </svg>
      );
    }

    if (type === "gear") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <circle cx="32" cy="32" r="9" />
          <path d="M32 10v7M32 47v7M10 32h7M47 32h7M16.5 16.5l5 5M42.5 42.5l5 5M47.5 16.5l-5 5M21.5 42.5l-5 5" />
          <path d="M27 12h10l2 6 6 3 6-2 5 8-5 4v7l5 4-5 8-6-2-6 3-2 6H27l-2-6-6-3-6 2-5-8 5-4v-7l-5-4 5-8 6 2 6-3 2-6Z" />
        </svg>
      );
    }

    if (type === "shield") {
      return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <path d="M32 9 49 16v13c0 12-7 20-17 26-10-6-17-14-17-26V16L32 9Z" />
          <path d="m23 31 6 6 13-14" />
        </svg>
      );
    }

    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="11" y="14" width="42" height="30" rx="3" />
        <path d="M23 52h18M32 44v8M18 30h8l4-8 6 17 4-9h7" />
      </svg>
    );
  };

  return (
    <section className="asx-technology asx-technology-v2">
      <div className="asx-technology-v2__ambient" aria-hidden="true" />

      <div className="container mxw-1650">
        <div className="asx-technology-v2__hero">
          <div className="asx-technology-v2__intro">
            <div className="asx-technology-v2__eyebrow">
              <span>Technology Expertise</span>
              <i />
              <b />
            </div>

            <h2>
              Technology <span>Expertise</span>
            </h2>

            <div className="asx-technology-v2__accent" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>

            <p>
              Modern ASIC &amp; SoC design demands a deep understanding of evolving
              technologies, engineering methodologies, and development environments.
              Digicomm&apos;s engineering teams bring extensive experience across
              contemporary ASIC &amp; SoC design practices, enabling solutions that
              align with complex product architectures, advanced technology nodes,
              and industry-standard development workflows.
            </p>
          </div>

          

        </div>
        <div className="asx-technology-v2__grid">
          {technologyGroups.map((group) => (
            <article className="asx-technology-v2__card" key={group.title}>
              <div className="asx-technology-v2__icon" aria-hidden="true">
                <span className="asx-technology-v2__icon-ring" />
                <TechnologyIcon type={group.icon} />
              </div>

              <div className="asx-technology-v2__content">
                <h3>{group.title}</h3>

                <div className="asx-technology-v2__title-line" aria-hidden="true">
                  <span />
                  <span />
                </div>

                <div className="asx-technology-v2__tags">
                  {group.items.map((item) => (
                    <span key={item}>
                      <i aria-hidden="true" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};


/* =========================================================
   SECTION 8 — WHY DIGICOMM
========================================================= */

const WhyDigicommSection: React.FC = () => {
  const items = [
    { title: "Specialized Semiconductor Focus", description: "Dedicated engineering expertise focused on ASIC & SoC design and the broader semiconductor product development lifecycle.", icon: "target" },
    { title: "Execution with Discipline", description: "Structured engineering methodologies and quality-driven execution that promote consistency and predictable outcomes.", icon: "gear" },
    { title: "Collaborative Engineering", description: "Engineering teams that integrate seamlessly with customer organizations through transparent communication and shared execution ownership.", icon: "team" },
    { title: "Scalable Engagement Models", description: "Flexible engineering support that adapts to evolving project requirements, team structures, and development priorities.", icon: "growth" },
    { title: "Commitment to Quality", description: "A continuous focus on engineering excellence, design integrity, and reliable delivery across every engagement.", icon: "shield" },
  ];

  const WhyIcon = ({ type }: { type: string }) => {
    if (type === "target") return <svg viewBox="0 0 64 64" aria-hidden="true"><circle cx="32" cy="32" r="21"/><circle cx="32" cy="32" r="12"/><circle cx="32" cy="32" r="4"/><path d="M32 5v10M32 49v10M5 32h10M49 32h10"/></svg>;
    if (type === "gear") return <svg viewBox="0 0 64 64" aria-hidden="true"><circle cx="32" cy="32" r="10"/><path d="M32 9v7M32 48v7M9 32h7M48 32h7M16 16l5 5M43 43l5 5M48 16l-5 5M21 43l-5 5"/><path d="M27 12h10l2 6 6 3 6-2 5 8-5 4v7l5 4-5 8-6-2-6 3-2 6H27l-2-6-6-3-6 2-5-8 5-4v-7l-5-4 5-8 6 2 6-3 2-6Z"/></svg>;
    if (type === "team") return <svg viewBox="0 0 64 64" aria-hidden="true"><circle cx="32" cy="20" r="7"/><circle cx="16" cy="25" r="5"/><circle cx="48" cy="25" r="5"/><path d="M20 51v-5c0-9 5-15 12-15s12 6 12 15v5M6 49v-4c0-7 4-11 10-11 3 0 6 1 8 3M58 49v-4c0-7-4-11-10-11-3 0-6 1-8 3"/></svg>;
    if (type === "growth") return <svg viewBox="0 0 64 64" aria-hidden="true"><rect x="10" y="40" width="9" height="14"/><rect x="27" y="31" width="9" height="23"/><rect x="44" y="20" width="9" height="34"/><path d="M10 32 24 19l10 7L53 8M44 8h9v9"/></svg>;
    return <svg viewBox="0 0 64 64" aria-hidden="true"><path d="M32 7 51 14v15c0 13-8 22-19 28-11-6-19-15-19-28V14L32 7Z"/><path d="m22 32 7 7 14-16"/></svg>;
  };

  return (
    <section className="wdx-section">
      <div className="wdx-container">
        <div className="wdx-left">
          <div className="wdx-eyebrow"><span>Why Digicomm</span><div className="wdx-eyebrow-line"><span className="wdx-eyebrow-dot" /></div></div>
          <h2 className="wdx-title">What Sets <span>Us Apart</span></h2>
          <div className="wdx-title-decoration" aria-hidden="true"><span /><span /><span /></div>
          <p className="wdx-description">Successful ASIC &amp; SoC design depends not only on technical expertise but also on disciplined execution, collaborative engineering, and the ability to translate architectural intent into implementation-ready designs. Digicomm combines specialized ASIC &amp; SoC design expertise, structured engineering practices, and close customer collaboration to support complex design programs with confidence, consistency, and reliability.</p>
          <div className="wdx-wave" aria-hidden="true"><span className="wdx-wave-line wdx-wave-line-1"/><span className="wdx-wave-line wdx-wave-line-2"/><span className="wdx-wave-line wdx-wave-line-3"/><span className="wdx-wave-line wdx-wave-line-4"/></div>
        </div>
        <div className="wdx-right">
          {items.map((item, index) => (
            <article className="wdx-card" key={item.title}>
              <div className="wdx-card-icon"><WhyIcon type={item.icon} /></div>
              <div className="wdx-number">{String(index + 1).padStart(2, "0")}</div>
              <div className="wdx-card-content"><h3>{item.title}</h3><p>{item.description}</p></div>
              <div className="wdx-card-arrow" aria-hidden="true">↗</div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

type RelatedService = {
  title: string;
  description: string;
  link: string;
  icon: "verify" | "physical" | "dft" | "silicon" | "sta" | "ip" | "automation" | "consulting" | "team";
};

const RelatedServiceIcon = ({ type }: { type: RelatedService["icon"] }) => {
  if (type === "verify") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="17" y="17" width="30" height="30" rx="3" />
        <path d="M23 10V17M32 10V17M41 10V17M23 47V54M32 47V54M41 47V54M10 23H17M10 32H17M10 41H17M47 23H54M47 32H54M47 41H54" />
        <path d="m24 32 5 5 11-12" />
      </svg>
    );
  }

  if (type === "physical") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="14" y="14" width="36" height="36" rx="3" />
        <path d="M22 22h8v8h-8zM34 22h8v8h-8zM22 34h8v8h-8zM34 34h8v8h-8z" />
        <path d="M10 24h4M10 40h4M50 24h4M50 40h4M24 10v4M40 10v4M24 50v4M40 50v4" />
      </svg>
    );
  }

  if (type === "dft") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="18" y="14" width="28" height="38" rx="3" />
        <path d="M25 14v-4h14v4M24 28l5 5 11-12M24 41h16" />
      </svg>
    );
  }

  if (type === "silicon") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <rect x="18" y="18" width="28" height="28" rx="3" />
        <rect x="24" y="24" width="16" height="16" rx="2" />
        <path d="M24 10v8M32 10v8M40 10v8M24 46v8M32 46v8M40 46v8M10 24h8M10 32h8M10 40h8M46 24h8M46 32h8M46 40h8" />
      </svg>
    );
  }

  if (type === "sta") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <path d="M9 34h9l4-10 7 22 7-31 6 19h13" />
        <circle cx="32" cy="32" r="23" />
      </svg>
    );
  }

  if (type === "ip") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="20" cy="20" r="6" />
        <circle cx="44" cy="20" r="6" />
        <circle cx="20" cy="44" r="6" />
        <circle cx="44" cy="44" r="6" />
        <path d="M26 20h12M20 26v12M44 26v12M26 44h12" />
      </svg>
    );
  }

  if (type === "automation") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="10" />
        <path d="M32 9v7M32 48v7M9 32h7M48 32h7M16 16l5 5M43 43l5 5M48 16l-5 5M21 43l-5 5" />
        <path d="M27 12h10l2 6 6 3 6-2 5 8-5 4v7l5 4-5 8-6-2-6 3-2 6H27l-2-6-6-3-6 2-5-8 5-4v-7l-5-4 5-8 6 2 6-3 2-6Z" />
      </svg>
    );
  }

  if (type === "consulting") {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="21" r="9" />
        <path d="M16 53v-5c0-10 7-17 16-17s16 7 16 17v5" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="19" r="6" />
      <circle cx="17" cy="23" r="5" />
      <circle cx="47" cy="23" r="5" />
      <path d="M21 49v-4c0-8 5-13 11-13s11 5 11 13v4" />
      <path d="M7 48v-3c0-7 4-11 10-11 3 0 5 1 7 3M57 48v-3c0-7-4-11-10-11-3 0-5 1-7 3" />
    </svg>
  );
};

const RelatedCard: React.FC<RelatedService> = ({ title, description, link, icon }) => (
  <article className="asx-related-v2__card">
    <div className="asx-related-v2__card-icon" aria-hidden="true">
      <RelatedServiceIcon type={icon} />
    </div>

    <div className="asx-related-v2__card-body">
      <h4>{title}</h4>

      <div className="asx-related-v2__mini-line" aria-hidden="true">
        <span />
        <span />
      </div>

      <p>{description}</p>

      <Link to={link}>
        Learn More <span>→</span>
      </Link>
    </div>
  </article>
);

const RelatedServicesSection: React.FC = () => {
  const coreServices: RelatedService[] = [
    {
      title: "Verification Engineering",
      description: "Ensuring functional correctness and design confidence before implementation.",
      link: "/services/verification",
      icon: "verify",
    },
    {
      title: "Physical Design",
      description: "Transforming verified designs into manufacturable silicon-ready layouts.",
      link: "/services/physical-design",
      icon: "physical",
    },
    {
      title: "Design for Test (DFT)",
      description:
        "Improving manufacturability and production readiness through structured test engineering.",
      link: "/services/dft",
      icon: "dft",
    },
    {
      title: "Silicon Realization",
      description:
        "Supporting successful implementation, validation, and reliable silicon deployment.",
      link: "/services/silicon-realization",
      icon: "silicon",
    },
  ];

  const specializedServices: RelatedService[] = [
    {
      title: "Static Timing Analysis (STA)",
      description:
        "Supporting timing closure and implementation readiness for complex ASIC & SoC designs.",
      link: "/services/static-timing-analysis",
      icon: "sta",
    },
    {
      title: "IP Integration",
      description:
        "Complementing complex SoC development through specialized engineering expertise.",
      link: "/services/asic-soc-design",
      icon: "ip",
    },
    {
      title: "EDA & Design Automation",
      description:
        "Improving engineering productivity through automation and optimized design workflows.",
      link: "/services/eda-design-automation",
      icon: "automation",
    },
  ];

  const engagementModels: RelatedService[] = [
    {
      title: "Engineering Consulting",
      description:
        "Access specialized engineering guidance for architecture, design optimization, and technical advisory.",
      link: "/contact",
      icon: "consulting",
    },
    {
      title: "Dedicated Engineering Teams",
      description:
        "Extend your engineering organization with scalable ASIC & SoC design expertise.",
      link: "/contact",
      icon: "team",
    },
  ];

  return (
    <section className="asx-related asx-related-v2">
      <div className="asx-related-v2__grid-bg" aria-hidden="true" />

      <div className="container mxw-1650">
        <div className="asx-related-v2__heading">
          <div className="asx-related-v2__eyebrow">
            <span>Our Engineering Services</span>
            <i />
            <b />
          </div>

          <h2>
            Related Core <span>Engineering Services</span>
          </h2>

          <p>
            A comprehensive portfolio of complementary engineering services that extend
            from ASIC &amp; SoC design to complete silicon realization.
          </p>
        </div>

        <div className="asx-related-v2__rows">
          <section className="asx-related-v2__row asx-related-v2__row--core">
            <div className="asx-related-v2__row-intro">
              <div className="asx-related-v2__row-icon" aria-hidden="true">
                <RelatedServiceIcon type="verify" />
              </div>

              <h3>
                Related Core
                <br />
                <span>Engineering Services</span>
              </h3>

              <div className="asx-related-v2__row-line" aria-hidden="true">
                <span />
              </div>

              <p>
                End-to-end core engineering services that form the foundation of
                successful silicon development.
              </p>
            </div>

            <div className="asx-related-v2__cards asx-related-v2__cards--four">
              {coreServices.map((service) => (
                <RelatedCard key={service.title} {...service} />
              ))}
            </div>
          </section>

          <section className="asx-related-v2__row asx-related-v2__row--specialized">
            <div className="asx-related-v2__row-intro">
              <div className="asx-related-v2__row-icon" aria-hidden="true">
                <RelatedServiceIcon type="ip" />
              </div>

              <h3>
                Related Specialized
                <br />
                <span>Engineering Capabilities</span>
              </h3>

              <div className="asx-related-v2__row-line" aria-hidden="true">
                <span />
              </div>

              <p>
                Specialized capabilities that address critical aspects of complex
                semiconductor development.
              </p>
            </div>

            <div className="asx-related-v2__cards asx-related-v2__cards--three">
              {specializedServices.map((service) => (
                <RelatedCard key={service.title} {...service} />
              ))}
            </div>
          </section>

          <section className="asx-related-v2__row asx-related-v2__row--engagement">
            <div className="asx-related-v2__row-intro">
              <div className="asx-related-v2__row-icon" aria-hidden="true">
                <RelatedServiceIcon type="team" />
              </div>

              <h3>
                Related Engineering
                <br />
                <span>Engagement Models</span>
              </h3>

              <div className="asx-related-v2__row-line" aria-hidden="true">
                <span />
              </div>

              <p>
                Flexible engagement models that adapt to your project needs and
                organization scale.
              </p>
            </div>

            <div className="asx-related-v2__cards asx-related-v2__cards--two">
              {engagementModels.map((service) => (
                <RelatedCard key={service.title} {...service} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
};

const ASICCallToActionSection: React.FC = () => (
  <section className="asx-cta">
    <div className="asx-cta__grid" />
    <div className="asx-cta__glow" />

    <div className="container mxw-1650">
      <div className="asx-cta__layout">
        <div>
          <h2>Let&apos;s Design the Future of Silicon Together</h2>
          <p>
            Whether you&apos;re architecting a new ASIC or SoC, enhancing an existing design, or
            expanding your engineering capabilities, Digicomm Semiconductor is ready to support
            your product development journey with specialized ASIC &amp; SoC Design expertise,
            disciplined execution, and dependable engineering delivery.
          </p>
        </div>

        <Link to="/contact" className="asx-button">
          Discuss Your ASIC &amp; SoC Design Requirements
          <span>↗</span>
        </Link>
      </div>
    </div>
  </section>
);

const ASICSoCDesignPage: React.FC = () => {
  useEffect(() => {
    document.title = "ASIC & SoC Design | Digicomm Semiconductor";
  }, []);

  return (
    <Fragment>
      <div className="asic-soc-design-page ai-agency core-service-redesign">
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle
              pageTitle="ASIC & SoC Design Services"
              pagesub="ASIC & SoC Design Services"
            />

            <ASICDesignHeroSection />
            <ServiceOverviewSection />
            <WhyItMattersSection />
            <CapabilitiesSection />
            <KeyBenefitsSection />
            <EngineeringApproachSection />
            <TechnologyExpertiseSection />
            <WhyDigicommSection />
            <RelatedServicesSection />
            <ASICCallToActionSection />
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default ASICSoCDesignPage; 
